/**
 * Gallery lightbox logic. Pure functions with the DOM injected structurally,
 * so this is unit-testable under vitest's node environment — the same pattern
 * theme.ts uses. The actual wiring lives in main.ts, which is DOM bootstrap
 * and covered live by e2e rather than by unit tests.
 */

export type LightboxSlide = {
  href: string;
  width: number;
  height: number;
  alt: string;
};

/**
 * Wraps at both ends: arrowing past the last photo returns to the first.
 * Guards count <= 0 so an empty gallery cannot produce NaN or divide by zero.
 */
export function stepIndex(current: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return (((current + delta) % count) + count) % count;
}

export type LightboxAction = 'next' | 'prev' | 'close' | null;

/**
 * Escape is also handled natively by <dialog>.showModal(), but mapping it here
 * keeps the whole key contract in one testable place rather than split between
 * this file and browser behaviour.
 */
export function keyAction(key: string): LightboxAction {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
    case 'PageDown':
      return 'next';
    case 'ArrowLeft':
    case 'ArrowUp':
    case 'PageUp':
      return 'prev';
    case 'Escape':
      return 'close';
    default:
      return null;
  }
}

type ElementLike = { getAttribute(name: string): string | null };
type AnchorLike = ElementLike & {
  querySelector(selector: string): ElementLike | null;
};

/**
 * Reads one slide off a generated `.gallery-item` anchor. Returns null rather
 * than a half-built slide when href is missing, so a malformed item is skipped
 * instead of opening an empty lightbox.
 *
 * Dimensions come from data-full-w/h, which the build-time generator writes
 * from photos.json. They are the LARGEST available variant, which differs per
 * photo — the 1200 tier only exists where the source was big enough.
 */
export function readSlide(anchor: AnchorLike): LightboxSlide | null {
  const href = anchor.getAttribute('href');
  if (!href) return null;
  const width = Number(anchor.getAttribute('data-full-w') ?? '') || 0;
  const height = Number(anchor.getAttribute('data-full-h') ?? '') || 0;
  const alt = anchor.querySelector('img')?.getAttribute('alt') ?? '';
  return { href, width, height, alt };
}

/**
 * "3 of 15" — not shown; the lightbox is deliberately just the photo. This is
 * announced on navigation via a visually-hidden live region in main.ts, which
 * is the only thing that tells a screen-reader user the photo changed while
 * focus sits on a nav button. An earlier comment claimed that announcement
 * happened when no live region existed at all; the comment was the bug, which
 * is why this one names where the region actually is.
 */
export function slideLabel(index: number, count: number): string {
  return `${index + 1} of ${count}`;
}
